/**
 * 🎨 素材库管理面板（环艺学姐专属 - 管理员）
 *
 * 功能：
 * - 类别管理：5 级树形结构、新增/重命名/移动/删除、素材数量统计
 * - 标签管理：标签字典的增删改（独立标签管理器）
 * - 素材管理：上传时选分类 + 打标签，列表可快速增删标签
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Plus, Trash2, Upload, ChevronRight, ChevronDown, Folder, Loader2,
  Pencil, Check, ArrowUp, ArrowDown, Tags, FolderTree, ImageIcon
} from 'lucide-react';
import { API_BASE_URL } from '../../constants';
import { getToken } from '../../api';
import CategoryTreeSelect, { computeCategoryPaths, flattenCategoryTree } from './CategoryTreeSelect';
import TagPicker from './TagPicker';
import TagManager from './TagManager';

const MAX_LEVEL = 5;

// 首页弹窗主题令牌（与 InviteCodeModal 等同款 CSS 变量，自动跟随深浅色切换）
const T = {
  panel: 'var(--bg-panel)',
  section: 'var(--modal-input-bg)',
  inputBg: 'var(--modal-input-bg)',
  border: 'var(--border-subtle)',
  hover: 'var(--bg-hover)',
  text: 'var(--text-primary)',
  text2: 'var(--text-secondary)',
  muted: 'var(--text-muted)',
  shadow: 'var(--shadow-panel)'
};

// 输入框/下拉框统一样式
const inputStyle = {
  width: '100%',
  padding: '8px',
  border: `1px solid ${T.border}`,
  borderRadius: '4px',
  fontSize: '13px',
  background: T.inputBg,
  color: T.text,
  boxSizing: 'border-box'
};

const MaterialLibraryAdminPanel = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState('categories'); // 'categories' | 'tags' | 'materials'

  // 类别
  const [categories, setCategories] = useState([]);       // 扁平列表
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');
  const [newCategoryParent, setNewCategoryParent] = useState(null); // 父节点 id（null = 一级）

  // 标签
  const [tagGroups, setTagGroups] = useState([]);
  const [uploadTags, setUploadTags] = useState([]);       // 已选标签名数组

  // 素材
  const [materials, setMaterials] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploading, setUploading] = useState(false);

  // 编辑状态
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const [loading, setLoading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null); // { type: 'ok'|'err', text }

  // 预览 URL 需要及时回收，避免内存泄漏
  useEffect(() => () => { if (uploadPreview) URL.revokeObjectURL(uploadPreview); }, [uploadPreview]);

  // 顶部提示条（替代 alert，不打断操作）
  useEffect(() => {
    if (!uploadMsg) return undefined;
    const timer = setTimeout(() => setUploadMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [uploadMsg]);

  const notify = useCallback((type, text) => setUploadMsg({ type, text }), []);

  // ==========================================================================
  //  数据获取
  // ==========================================================================

  // 获取完整类别树（一次性拉取所有层级）
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/material-categories/tree`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '获取分类失败');
      setCategories(data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      notify('err', err.message || '获取分类失败');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  // 获取标签字典
  const fetchTags = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/material-tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '获取标签失败');
      setTagGroups(data.groups || []);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      notify('err', err.message || '获取标签失败');
    }
  }, [notify]);

  // 获取素材列表
  const fetchMaterials = useCallback(async (categoryId) => {
    if (!categoryId) return;
    try {
      const token = getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/huanshe/materials?category_id=${categoryId}&limit=200`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '获取素材失败');
      setMaterials(data.items || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
      notify('err', err.message || '获取素材失败');
    }
  }, [notify]);

  const refreshTagData = useCallback(async () => {
    await fetchTags();
    if (uploadCategory) await fetchMaterials(uploadCategory);
  }, [fetchTags, fetchMaterials, uploadCategory]);

  useEffect(() => {
    if (!open) return;
    fetchCategories();
    fetchTags();
  }, [open, fetchCategories, fetchTags]);

  // 派生：树 + 扁平列表 + 路径映射
  // 后端 /material-categories/tree 已直接返回嵌套树
  const tree = useMemo(() => categories || [], [categories]);
  const flatCategories = useMemo(() => flattenCategoryTree(tree), [tree]);
  const paths = useMemo(() => computeCategoryPaths(flatCategories), [flatCategories]);

  // 当前选中类别的父级链（用于新增子类时自动带上父级）
  const parentNode = useMemo(
    () => flatCategories.find(c => c.id === newCategoryParent) || null,
    [flatCategories, newCategoryParent]
  );

  // 新增子类时可选的父节点（未超过 5 级的节点）
  const availableParents = useMemo(
    () => flatCategories.filter(c => (c.level || 1) < MAX_LEVEL),
    [flatCategories]
  );

  // ==========================================================================
  //  类别操作
  // ==========================================================================

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      notify('err', '请输入类别名称');
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/material-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newCategoryName.trim(),
          parent_id: newCategoryParent || null,
          description: newCategoryDesc.trim() || null
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '创建类别失败');

      setNewCategoryName('');
      setNewCategoryDesc('');
      notify('ok', `类别「${newCategoryName.trim()}」已创建`);
      await fetchCategories();
    } catch (err) {
      notify('err', err.message || '创建类别失败');
    } finally {
      setLoading(false);
    }
  };

  const startEditCategory = (node) => {
    setEditingId(node.id);
    setEditingName(node.name);
  };

  const cancelEditCategory = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveEditCategory = async (node) => {
    const name = editingName.trim();
    if (!name) {
      notify('err', '名称不能为空');
      return;
    }
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/material-categories/${node.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '更新类别失败');
      cancelEditCategory();
      notify('ok', '类别已重命名');
      await fetchCategories();
    } catch (err) {
      notify('err', err.message || '更新类别失败');
    }
  };

  const deleteCategory = async (node) => {
    if (!window.confirm(`确认删除类别「${node.name}」？`)) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/material-categories/${node.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (res.status === 409) {
        const detail = data.material_count ? `${data.material_count} 个素材` : '';
        const childDetail = data.child_count ? `${data.child_count} 个子分类` : '';
        const parts = [detail, childDetail].filter(Boolean).join('、');
        if (!window.confirm(`该分类下还有 ${parts}，删除将一并移除。确认强制删除？`)) return;

        const forceRes = await fetch(`${API_BASE_URL}/api/huanshe/admin/material-categories/${node.id}?force=1`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const forceData = await forceRes.json();
        if (!forceRes.ok) throw new Error(forceData.error || '删除类别失败');
        notify('ok', `已删除 ${forceData.deleted_categories || 1} 个分类、${forceData.deleted_materials || 0} 个素材`);
        await fetchCategories();
        return;
      }

      if (!res.ok) throw new Error(data.error || '删除类别失败');
      notify('ok', '类别已删除');
      await fetchCategories();
    } catch (err) {
      notify('err', err.message || '删除类别失败');
    }
  };

  // 同级上移 / 下移
  const moveCategory = async (node, direction) => {
    const siblings = flatCategories
      .filter(c => (c.parent_id || null) === (node.parent_id || null))
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const idx = siblings.findIndex(s => s.id === node.id);
    const target = siblings[idx + direction];
    if (!target) return;

    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/material-categories/${node.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ display_order: target.display_order || 0 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '调整顺序失败');

      // 目标与当前交换 display_order
      await fetch(`${API_BASE_URL}/api/huanshe/admin/material-categories/${target.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ display_order: node.display_order || 0 })
      });

      await fetchCategories();
    } catch (err) {
      notify('err', err.message || '调整顺序失败');
    }
  };

  // 在指定节点下新建子类（树节点上的 + 按钮）
  const addSubCategory = (node) => {
    setNewCategoryParent(node.id);
    setNewCategoryName('');
    setNewCategoryDesc('');
    setActiveTab('categories');
    // 滚动到表单
    setTimeout(() => {
      document.getElementById('category-create-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  // ==========================================================================
  //  标签操作
  // ==========================================================================

  const createTag = async (name, group) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, group_name: group })
      });
      const data = await res.json();
      if (!res.ok) return null;
      await fetchTags();
      return data;
    } catch (err) {
      console.error('Failed to create tag:', err);
      return null;
    }
  };

  // 快速给素材加/减标签
  const toggleMaterialTag = async (material, tagName) => {
    const has = (material.tags || []).includes(tagName);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/materials/${material.id}/tags`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(has ? { remove: [tagName] } : { add: [tagName] })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '更新标签失败');

      // 本地乐观更新
      setMaterials(prev => prev.map(m =>
        m.id === material.id
          ? { ...m, tags: has ? m.tags.filter(t => t !== tagName) : [...(m.tags || []), tagName] }
          : m
      ));
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '更新标签失败');
    }
  };

  // ==========================================================================
  //  素材操作
  // ==========================================================================

  const uploadMaterial = async () => {
    if (!uploadFile) {
      notify('err', '请选择 PNG 文件');
      return;
    }
    if (!uploadName.trim()) {
      notify('err', '请输入素材名称');
      return;
    }
    if (!uploadCategory) {
      notify('err', '请选择所属分类');
      return;
    }

    setUploading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName.trim());
      formData.append('category_id', uploadCategory);
      formData.append('tags', JSON.stringify(uploadTags));

      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/materials/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '上传失败');

      notify('ok', `素材「${uploadName.trim()}」上传成功`);
      setUploadFile(null);
      setUploadPreview('');
      setUploadName('');
      setUploadTags([]);
      await fetchMaterials(uploadCategory);
      await fetchCategories();
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const deleteMaterial = async (id, name) => {
    if (!window.confirm(`确认删除素材「${name}」？`)) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/huanshe/admin/materials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '删除素材失败');
      notify('ok', '素材已删除');
      await fetchMaterials(uploadCategory);
      await fetchCategories();
      await fetchTags();
    } catch (err) {
      notify('err', err.message || '删除素材失败');
    }
  };

  if (!open) return null;

  // ==========================================================================
  //  渲染：类别树节点
  // ==========================================================================
  const renderCategoryNode = (node, level = 0) => {
    const isExpanded = expandedCategories.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isEditing = editingId === node.id;
    const atMaxLevel = (node.level || level + 1) >= MAX_LEVEL;

    return (
      <div key={node.id}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 10px',
          paddingLeft: `${level * 18 + 10}px`,
          borderRadius: '4px',
          transition: 'background 0.2s'
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = T.hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          {hasChildren ? (
            <span
              onClick={() => {
                const newExpanded = new Set(expandedCategories);
                if (isExpanded) newExpanded.delete(node.id);
                else newExpanded.add(node.id);
                setExpandedCategories(newExpanded);
              }}
              style={{ marginRight: '6px', cursor: 'pointer', color: T.text2, display: 'flex' }}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          ) : (
            <span style={{ width: '14px', marginRight: '6px' }} />
          )}

          <Folder size={14} style={{ marginRight: '6px', color: '#f59e0b', flexShrink: 0 }} />

          {isEditing ? (
            <>
              <input
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); saveEditCategory(node); }
                  if (e.key === 'Escape') cancelEditCategory();
                }}
                autoFocus
                style={{ ...inputStyle, width: '140px', padding: '3px 6px' }}
              />
              <button
                onClick={() => saveEditCategory(node)}
                style={{ marginLeft: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#34d399', display: 'flex', padding: 0 }}
                title="保存"
              >
                <Check size={13} />
              </button>
              <button
                onClick={cancelEditCategory}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, display: 'flex', padding: 0 }}
                title="取消"
              >
                <X size={13} />
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: '13px', color: T.text, marginRight: '6px' }}>{node.name}</span>
              <span style={{
                fontSize: '10px',
                padding: '1px 5px',
                borderRadius: '8px',
                background: T.hover,
                color: T.muted,
                marginRight: '4px'
              }}>
                L{node.level || level + 1}
              </span>
              {node.material_count > 0 && (
                <span style={{
                  fontSize: '10px',
                  padding: '1px 5px',
                  borderRadius: '8px',
                  background: 'rgba(16,185,129,0.15)',
                  color: '#34d399',
                  marginRight: '4px'
                }}>
                  {node.material_count}
                </span>
              )}
            </>
          )}

          <span style={{ flex: 1 }} />

          <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
            {!atMaxLevel && (
              <button
                onClick={(e) => { e.stopPropagation(); addSubCategory(node); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text2, display: 'flex', padding: '3px' }}
                title="添加子类"
              >
                <Plus size={13} />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); moveCategory(node, -1); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text2, display: 'flex', padding: '3px' }}
              title="上移"
            >
              <ArrowUp size={13} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); moveCategory(node, 1); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text2, display: 'flex', padding: '3px' }}
              title="下移"
            >
              <ArrowDown size={13} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); startEditCategory(node); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text2, display: 'flex', padding: '3px' }}
              title="重命名"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); deleteCategory(node); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', display: 'flex', padding: '3px' }}
              title="删除"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderCategoryNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { key: 'categories', label: '类别管理', icon: FolderTree },
    { key: 'materials', label: '素材管理', icon: ImageIcon },
    { key: 'tags', label: '标签管理', icon: Tags }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div className="material-admin-panel" style={{
        background: T.panel,
        border: `1px solid ${T.border}`,
        borderRadius: '8px',
        width: '960px',
        height: 'min(760px, calc(100vh - 40px))',
        minHeight: '560px',
        maxHeight: 'calc(100vh - 40px)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: T.shadow,
        color: T.text
      }}>
        {/* 标题栏 */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: T.text }}>
            素材库管理
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: T.text2,
              display: 'flex'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab 切换 */}
        <div style={{
          display: 'flex',
          borderBottom: `1px solid ${T.border}`,
          padding: '0 20px'
        }}>
          {tabs.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  borderBottom: active ? '2px solid #3b82f6' : 'none',
                  fontWeight: active ? 600 : 400,
                  color: active ? '#3b82f6' : T.text2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* 全局提示条 */}
        {uploadMsg && (
          <div style={{
            margin: '12px 20px 0',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            background: uploadMsg.type === 'ok' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            color: uploadMsg.type === 'ok' ? '#34d399' : '#f87171',
            border: `1px solid ${uploadMsg.type === 'ok' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`
          }}>
            {uploadMsg.text}
          </div>
        )}

        {/* 内容区域 */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {/* ═══════════ 类别管理 ═══════════ */}
          {activeTab === 'categories' && (
            <div>
              {/* 新增类别表单 */}
              <div id="category-create-form" style={{
                background: T.section,
                border: `1px solid ${T.border}`,
                padding: '16px',
                borderRadius: '6px',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: T.text }}>
                  新增类别
                  <span style={{ fontSize: '11px', fontWeight: 400, color: T.muted, marginLeft: '8px' }}>
                    最多 {MAX_LEVEL} 级
                  </span>
                </h3>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: T.text2 }}>
                    上级分类
                    {parentNode && (
                      <span style={{ fontSize: '11px', color: T.muted, marginLeft: '6px' }}>
                        当前路径：{paths.get(parentNode.id)}
                      </span>
                    )}
                  </label>
                  <select
                    value={newCategoryParent || ''}
                    onChange={(e) => setNewCategoryParent(e.target.value ? parseInt(e.target.value, 10) : null)}
                    style={inputStyle}
                  >
                    <option value="">无（一级分类）</option>
                    {availableParents.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {paths.get(cat.id) || cat.name}
                      </option>
                    ))}
                  </select>
                  {parentNode && (parentNode.level || 1) >= MAX_LEVEL && (
                    <div style={{ fontSize: '11px', color: '#f87171', marginTop: '4px' }}>
                      该分类已达 {MAX_LEVEL} 级上限，无法再添加子类
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: T.text2 }}>类别名称 *</label>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="如：单人沙发"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: T.text2 }}>描述</label>
                    <input
                      type="text"
                      value={newCategoryDesc}
                      onChange={(e) => setNewCategoryDesc(e.target.value)}
                      placeholder="可选"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <button
                  onClick={createCategory}
                  disabled={loading}
                  style={{
                    padding: '8px 16px',
                    background: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {loading ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
                  创建类别
                </button>
              </div>

              {/* 类别树 */}
              <div style={{
                border: `1px solid ${T.border}`,
                borderRadius: '6px',
                minHeight: '300px'
              }}>
                <div style={{
                  padding: '12px',
                  borderBottom: `1px solid ${T.border}`,
                  background: T.section,
                  color: T.text,
                  fontWeight: 600,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>类别结构（共 {flatCategories.length} 个）</span>
                  <button
                    onClick={() => {
                      const newExpanded = new Set(flatCategories.map(c => c.id));
                      setExpandedCategories(newExpanded);
                    }}
                    style={{
                      fontSize: '11px',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      border: `1px solid ${T.border}`,
                      background: 'transparent',
                      color: T.text2,
                      cursor: 'pointer'
                    }}
                  >
                    全部展开
                  </button>
                </div>
                <div style={{ padding: '8px' }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: T.muted }}>
                      <Loader2 size={24} className="spin" />
                    </div>
                  ) : tree.length > 0 ? (
                    tree.map(node => renderCategoryNode(node))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: T.muted }}>
                      暂无类别
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ═══════════ 标签管理 ═══════════ */}
          {activeTab === 'tags' && (
            <TagManager onMessage={notify} onTagsChanged={refreshTagData} />
          )}

          {/* ═══════════ 素材管理 ═══════════ */}
          {activeTab === 'materials' && (
            <div>
              {/* 上传素材表单 */}
              <div style={{
                background: T.section,
                border: `1px solid ${T.border}`,
                padding: '16px',
                borderRadius: '6px',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: T.text }}>上传素材</h3>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: T.text2 }}>素材名称 *</label>
                  <input
                    type="text"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="如：北欧单人布艺沙发"
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: T.text2 }}>
                    所属分类 *
                    <span style={{ fontSize: '11px', color: T.muted, marginLeft: '6px' }}>
                      可选到第 {MAX_LEVEL} 级
                    </span>
                  </label>
                  <CategoryTreeSelect
                    tree={tree}
                    paths={paths}
                    value={uploadCategory ? parseInt(uploadCategory, 10) : null}
                    onChange={(id) => {
                      setUploadCategory(id ? String(id) : '');
                      if (id) fetchMaterials(id);
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: T.text2 }}>PNG 文件 *</label>
                  {/* 🎨 选完立刻本地预览（选中的素材直接可见），复用 uploadFile + uploadPreview */}
                  {uploadPreview && (
                    <div style={{
                      width: '100%', height: '120px', background: T.inputBg, borderRadius: '4px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden', marginBottom: '8px'
                    }}>
                      <img src={uploadPreview} alt="预览" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                  )}
                  <input
                    id="material-upload-input"
                    type="file"
                    accept="image/png"
                    onChange={(e) => {
                      const f = e.target.files[0];
                      setUploadFile(f || null);
                      if (uploadPreview) { URL.revokeObjectURL(uploadPreview); setUploadPreview(''); }
                      if (f) setUploadPreview(URL.createObjectURL(f));
                    }}
                    style={{ fontSize: '13px', color: T.text2 }}
                  />
                </div>

                <button
                  onClick={uploadMaterial}
                  disabled={uploading}
                  style={{
                    padding: '8px 16px',
                    background: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {uploading ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
                  {uploading ? '上传中...' : '上传素材'}
                </button>

                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: T.text2 }}>
                    标签
                    <span style={{ fontSize: '11px', color: T.muted, marginLeft: '6px' }}>
                      颜色 / 材质 / 风格等
                    </span>
                  </label>
                  <TagPicker
                    groups={tagGroups}
                    selected={uploadTags}
                    onChange={setUploadTags}
                    onCreateTag={createTag}
                  />
                </div>
              </div>

              {/* 素材列表 */}
              {uploadCategory && (
                <div style={{
                  border: `1px solid ${T.border}`,
                  borderRadius: '6px'
                }}>
                  <div style={{
                    padding: '12px',
                    borderBottom: `1px solid ${T.border}`,
                    background: T.section,
                    color: T.text,
                    fontWeight: 600,
                    fontSize: '14px'
                  }}>
                    素材列表（共 {materials.length} 个）
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    padding: '12px'
                  }}>
                    {materials.map(material => (
                      <div key={material.id} style={{
                        border: `1px solid ${T.border}`,
                        borderRadius: '6px',
                        padding: '8px'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '120px',
                          background: T.inputBg,
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          marginBottom: '8px'
                        }}>
                          <img
                            src={material.thumbnail_url || material.png_url}
                            alt={material.name}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '4px', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {material.name}
                        </div>
                        <div style={{ fontSize: '11px', color: T.muted, marginBottom: '8px' }}>
                          {material.width} × {material.height}
                        </div>

                        {/* 已打标签（点击 ✕ 移除） */}
                        {(material.tags || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                            {material.tags.map(t => (
                              <span
                                key={t}
                                onClick={() => toggleMaterialTag(material, t)}
                                title="点击移除该标签"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  padding: '2px 6px',
                                  fontSize: '10px',
                                  borderRadius: '10px',
                                  background: 'rgba(59,130,246,0.15)',
                                  border: '1px solid rgba(59,130,246,0.35)',
                                  color: '#60a5fa',
                                  cursor: 'pointer'
                                }}
                              >
                                {t}
                                <X size={9} />
                              </span>
                            ))}
                          </div>
                        )}

                        {/* 快速加标签 */}
                        <div style={{ position: 'relative', marginBottom: '8px' }}>
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value) toggleMaterialTag(material, e.target.value);
                            }}
                            style={{
                              ...inputStyle,
                              padding: '4px 6px',
                              fontSize: '11px'
                            }}
                          >
                            <option value="">+ 添加标签</option>
                            {tagGroups.map(g => (
                              <optgroup key={g.name} label={g.name}>
                                {(g.tags || [])
                                  .filter(t => !(material.tags || []).includes(t.name))
                                  .map(t => (
                                    <option key={t.id} value={t.name}>{t.name}</option>
                                  ))}
                              </optgroup>
                            ))}
                          </select>
                        </div>

                        <button
                          onClick={() => deleteMaterial(material.id, material.name)}
                          style={{
                            width: '100%',
                            padding: '4px',
                            background: '#ef4444',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                          }}
                        >
                          <Trash2 size={10} />
                          删除
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        /* 下拉选项跟随主题（原生 option 默认白底，深色模式下会白底白字） */
        .material-admin-panel select option {
          background: var(--bg-panel);
          color: var(--text-primary);
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MaterialLibraryAdminPanel;
